import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useApp } from "../context/AppContext";
import { cn } from "../lib/utils";
import { AtSign, Check } from "lucide-react";
import { Avatar } from "./board/BoardCells";
import { timeAgo, personOf, renderWithMentions } from "./board/social";

export default function MyMentions() {
  const { t, lang, entities, refreshInbox } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], unread_count: 0 });

  const load = useCallback(async () => {
    try { const { data } = await api.get("/notifications?limit=12"); setData(data); } catch { /* noop */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const open = async (n) => {
    if (!n.read) { try { await api.post("/notifications/read", { ids: [n.id] }); } catch { /* noop */ } }
    const key = entities.find((e) => e.id === n.entity_id)?.key;
    if (key && n.space_id && n.project_id) {
      navigate(`/entity/${key}/space/${n.space_id}/project/${n.project_id}?item=${n.item_id}`);
    }
    load();
  };
  const markAll = async () => { try { await api.post("/notifications/read-all"); } catch { /* noop */ } load(); refreshInbox?.(); };
  const dismiss = async (e, n) => {
    e.stopPropagation();
    try { await api.post("/notifications/dismiss", { ids: [n.id] }); } catch { /* noop */ }
    load(); refreshInbox?.();
  };

  if (!data.items.length) return null;

  return (
    <div className="mb-6 rounded-2xl border bg-surface" data-testid="my-mentions">
      <div className="flex items-center gap-2 px-4 py-3 border-b">
        <AtSign className="w-4 h-4 text-primary" />
        <h2 className="text-sm font-semibold text-text-primary">{t("mentions_title")}</h2>
        {data.unread_count > 0 && (
          <span className="text-[11px] font-bold px-1.5 py-0.5 rounded-full bg-primary text-white" data-testid="mentions-unread-count">{data.unread_count}</span>
        )}
        <div className="flex-1" />
        {data.unread_count > 0 && (
          <button onClick={markAll} data-testid="mentions-mark-all" className="inline-flex items-center gap-1 text-[11px] font-medium text-text-secondary hover:text-text-primary transition-colors">
            <Check className="w-3.5 h-3.5" />{t("mentions_mark_all")}
          </button>
        )}
      </div>
      <div className="divide-y">
        {data.items.map((n) => (
          <div key={n.id} data-testid={`mention-${n.id}`}
            className={cn("group w-full flex items-start gap-3 px-4 py-3 hover:bg-surface-hover transition-colors", !n.read && "bg-primary/[0.04]")}>
            <button onClick={() => open(n)} data-testid={`mention-open-${n.id}`}
              className="flex-1 min-w-0 flex items-start gap-3 text-left">
              {!n.read && <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary shrink-0" />}
              <span className={cn("shrink-0", n.read && "ml-3.5")}><Avatar person={personOf(n)} size={28} /></span>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-text-primary">
                  <span className="font-semibold">{n.actor_name}</span> {t("mentions_on")} <span className="font-medium">{n.item_name}</span>
                </p>
                <p className="text-[13px] text-text-secondary truncate">{renderWithMentions(n.text, [])}</p>
              </div>
            </button>
            <span className="text-[11px] text-text-secondary shrink-0 mt-0.5">{timeAgo(n.created_at, lang)}</span>
            <button onClick={(e) => dismiss(e, n)} title={t("mentions_dismiss")} aria-label={t("mentions_dismiss")}
              data-testid={`mention-dismiss-${n.id}`}
              className="shrink-0 mt-0.5 w-6 h-6 rounded-full flex items-center justify-center text-text-secondary hover:text-white hover:bg-primary transition-colors opacity-0 group-hover:opacity-100 focus-visible:opacity-100">
              <Check className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}
